import axios from 'axios'

// Тексты ошибок для пользователя: без технических подробностей, их место — консоль
const FAILURE_MESSAGE = 'Не удалось включить браузерные уведомления. Попробуйте ещё раз.'
const UNSUPPORTED_MESSAGE = 'Браузер не поддерживает уведомления. Откройте систему в другом браузере.'

/**
 * Composable для регистрации Service Worker и подписки на Web Push уведомления.
 * Используется в App.vue при монтировании после авторизации.
 */
export function usePushNotifications() {
  /**
   * Зарегистрировать Service Worker
   */
  async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker не поддерживается браузером')
      return null
    }

    try {
      return await navigator.serviceWorker.register('/sw.js')
    } catch (error) {
      console.error('Ошибка регистрации Service Worker:', error)
      return null
    }
  }

  /**
   * Подписать браузер на push-уведомления
   *
   * @returns {Promise<{success: boolean, permission: string, message: (string|null)}>}
   *   message — готовый текст ошибки для показа пользователю (null, если показывать нечего)
   */
  async function subscribeToPush(registration) {
    if (!('PushManager' in window)) {
      console.log('[Push] Браузер не поддерживает Push API')
      return { success: false, permission: 'unsupported', message: UNSUPPORTED_MESSAGE }
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      // Пользователь сам отказал или заблокировал — состояние видно по баннеру, тост не нужен
      console.log('[Push] Уведомления заблокированы в настройках браузера (' + permission + ')')
      return { success: false, permission: permission, message: null }
    }

    try {
      // Публичный VAPID-ключ берём с сервера (без пересборки фронта при ротации)
      const response = await axios.get('/api/push-subscriptions/vapid-key')
      const vapidPublicKey = response.data.key

      if (!vapidPublicKey) {
        console.log('[Push] VAPID-ключ не настроен')
        return {
          success: false,
          permission: permission,
          message: 'Браузерные уведомления пока не настроены. Обратитесь к администратору.',
        }
      }

      let subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        })
      }

      await sendSubscriptionToServer(subscription)
      console.log('[Push] Подписка активна')
      return { success: true, permission: permission, message: null }
    } catch (error) {
      // Технические подробности — в консоль, пользователю — человеческий текст
      console.error('[Push] Ошибка подписки:', error)
      return { success: false, permission: permission, message: FAILURE_MESSAGE }
    }
  }

  /**
   * Отправить подписку на бэкенд
   */
  async function sendSubscriptionToServer(subscription) {
    try {
      await axios.post('/api/push-subscriptions/subscribe', {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth')))),
        },
      })
    } catch (error) {
      console.error('Ошибка отправки push-подписки на сервер:', error)
      // Подписка не сохранена на сервере — уведомления не придут,
      // поэтому ошибку не глотаем, а отдаём наверх для показа пользователю
      throw error
    }
  }

  /**
   * Отписаться от push-уведомлений (при логауте)
   */
  async function unsubscribe() {
    if (!('serviceWorker' in navigator)) return

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await axios.post('/api/push-subscriptions/unsubscribe', {
          endpoint: subscription.endpoint,
        })
        await subscription.unsubscribe()
      }
    } catch (error) {
      console.error('Ошибка отписки от push:', error)
    }
  }

  /**
   * Зарегистрировать SW и подписаться на push (вызывается в mounted)
   *
   * @returns {Promise<{success: boolean, permission: string, message: (string|null)}>}
   */
  async function registerAndSubscribe() {
    const registration = await registerServiceWorker()
    if (!registration) {
      console.log('[Push] SW не зарегистрирован')
      return { success: false, permission: getPermissionStatus(), message: FAILURE_MESSAGE }
    }
    return await subscribeToPush(registration)
  }

  /**
   * Текущий статус разрешения push-уведомлений
   * @returns {'granted'|'denied'|'default'|'unsupported'}
   */
  function getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'unsupported'
    }
    return Notification.permission
  }

  /**
   * Запросить разрешение и подписаться на push-уведомления
   *
   * @returns {Promise<{success: boolean, permission: string, message: (string|null)}>}
   */
  async function requestPermissionAndSubscribe() {
    if (!('Notification' in window)) {
      return { success: false, permission: 'unsupported', message: UNSUPPORTED_MESSAGE }
    }
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      // Отказ пользователя объясняет сам баннер — тостом не дублируем
      return { success: false, permission: permission, message: null }
    }
    return await registerAndSubscribe()
  }

  return {
    registerAndSubscribe,
    unsubscribe,
    getPermissionStatus,
    requestPermissionAndSubscribe,
  }
}

/**
 * Конвертация base64url строки VAPID-ключа в Uint8Array для PushManager.subscribe()
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}
